import { FC, MouseEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Button, Col, Dropdown, Input, InputNumber, Menu, Row, Segmented } from 'antd';
import { ListFilter, TextQuote } from 'lucide-react';
import {
    TbCalendar,
    TbCheck,
    TbChevronRight,
    TbDeviceFloppy,
    TbFilterPlus,
    TbHash,
    TbLetterCase,
    TbPlus,
    TbSearch,
    TbX,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMaps } from '@/hooks/useMaps';
import {
    ADVANCED_FILTERS_MINIMUM_PLAN,
    AdvancedFilterEditor,
    createAdvancedFilterDefinition,
    DropdownMenuLabel,
    DropdownNestedHeader,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    FilterEntryButton,
    getAdvancedFilterCounts,
    isAdvancedFilterDefinitionComplete,
    LearnMoreFooter,
    NotAvailable,
    type AdvancedFilterDefinition,
} from '@/shared';
import { runAfterPlatformDragCancellation } from '@/shared/components/drag-and-drop';
import type { DropdownOptionAvatarProps } from '@/shared/components/dropdowns/DropdownOptionContent';
import {
    FinancialCompanySelect,
    PlatformForm as Form,
    PlatformDatePicker,
    PlatformSelect as Select,
} from '@/shared/components/forms';
import { ListViewColumn, ListViewColumnDataType } from '@/types';
import dayjs, { serializeDatePickerValues } from '@/utilities/dayjs';

import FilterButtonItem from '../02-AppliedFilters/FilterButtonItem';
import FilterValueSelect from './components/FilterValueSelect';
import { normalizeListFilterValues } from './listFilterLogic';

export { buildListFilterExpression, buildListFilterJsonLogic, normalizeListFilterValues } from './listFilterLogic';

interface FilterDropdownProps {
    advancedFilter?: AdvancedFilterDefinition<ListFilterRule>;
    onAdvancedFilterChange?: (definition: AdvancedFilterDefinition<ListFilterRule> | undefined) => void;
    openToAdvancedFilters?: boolean;
    docsLabel?: string;
    emptyColumnsDescription?: string;
    filterableResourceLabel?: string;
    filters: ListFilterRule[];
    setFilters: (next: ListFilterRule[] | null) => void;
    showAdvancedFilters?: boolean;
    trigger?: ReactNode;
    columns: FilterableColumn[];
}

export type ListFilterRule = {
    dataType?: ListViewColumnDataType;
    field: string;
    fieldLabel: string;
    id: string;
    operator: ListFilterOperator;
    operatorLabel: string;
    value: ListFilterValue;
    valueJoin?: ListFilterValueJoin;
};

export type ListFilterScalarValue = boolean | number | string;
export type ListFilterValue = ListFilterScalarValue | ListFilterScalarValue[];
export type ListFilterValueJoin = 'and' | 'or';

export type ListFiltersQueryValue =
    | {
          advancedFilter?: AdvancedFilterDefinition<ListFilterRule>;
          jsonLogic?: import('@/shared/components/filters').JsonLogicRule;
          rules?: ListFilterRule[];
      }
    | ListFilterRule[]
    | null
    | undefined;

export type ListFilterOperator = 'contains' | 'equals' | 'greaterThan' | 'lessThan' | 'notEquals';
export type ListFilterOption = {
    avatar?: DropdownOptionAvatarProps;
    description?: ReactNode;
    icon?: ReactNode;
    label: ReactNode;
    metadata?: ReactNode[];
    searchText?: string;
    value: ListFilterScalarValue;
};
export type FilterableColumn = Pick<ListViewColumn, 'dataType' | 'id' | 'isHidden' | 'key' | 'label' | 'order'> & {
    allowMultipleValues?: boolean;
    filterOperators?: ListFilterOperator[];
    filterOptions?: ListFilterOption[];
    filterValueEditor?: 'company' | 'default';
    icon?: ReactNode;
};

export interface FilterableColumnInput {
    allowMultipleValues?: boolean;
    dataType: ListViewColumnDataType;
    filterOperators?: ListFilterOperator[];
    filterOptions?: FilterableColumn['filterOptions'];
    filterValueEditor?: FilterableColumn['filterValueEditor'];
    icon?: ReactNode;
    key: string;
    label: string;
    order: number;
}

export const createFilterableColumn = (input: FilterableColumnInput): FilterableColumn => ({
    allowMultipleValues: input.allowMultipleValues,
    dataType: input.dataType,
    filterOperators: input.filterOperators,
    filterOptions: input.filterOptions,
    filterValueEditor: input.filterValueEditor,
    icon: input.icon,
    id: input.key,
    isHidden: false,
    key: input.key,
    label: input.label,
    order: input.order,
});

const TEXT_OPERATORS: { label: string; value: ListFilterOperator }[] = [
    { label: 'Contains', value: 'contains' },
    { label: 'Equals', value: 'equals' },
    { label: 'Does Not Equal', value: 'notEquals' },
];

const NUMBER_OPERATORS: { label: string; value: ListFilterOperator }[] = [
    { label: 'Equals', value: 'equals' },
    { label: 'Greater Than', value: 'greaterThan' },
    { label: 'Less Than', value: 'lessThan' },
];

const DATE_OPERATORS: { label: string; value: ListFilterOperator }[] = [
    { label: 'On', value: 'equals' },
    { label: 'After', value: 'greaterThan' },
    { label: 'Before', value: 'lessThan' },
];

const BOOLEAN_OPERATORS: { label: string; value: ListFilterOperator }[] = [{ label: 'Equals', value: 'equals' }];

export const numericDataTypes = new Set<ListViewColumnDataType>([
    ListViewColumnDataType.CURRENCY,
    ListViewColumnDataType.NUMBER,
    ListViewColumnDataType.PERCENTAGE,
    ListViewColumnDataType.YEAR,
]);

export const dateDataTypes = new Set<ListViewColumnDataType>([
    ListViewColumnDataType.DATE,
    ListViewColumnDataType.DATE_RANGE,
    ListViewColumnDataType.MONTH,
    ListViewColumnDataType.QUARTER,
]);

export const hasListFilterValue = (value: ListFilterValue | null | undefined) =>
    normalizeListFilterValues(value).length > 0;

export const coerceListFilterValuesForDataType = (
    dataType: ListViewColumnDataType | undefined,
    value: ListFilterValue | null | undefined,
): ListFilterScalarValue[] => {
    const values = normalizeListFilterValues(value);

    if (dataType === ListViewColumnDataType.BOOLEAN) {
        return values
            .map((item) => {
                if (item === true || item === 'true') {
                    return true;
                }
                if (item === false || item === 'false') {
                    return false;
                }

                return undefined;
            })
            .filter((item): item is boolean => item !== undefined);
    }

    if (dataType && numericDataTypes.has(dataType)) {
        return values
            .map((item) => (typeof item === 'number' ? item : Number(String(item).replace(/[$,%\s,]/g, ''))))
            .filter((item) => Number.isFinite(item));
    }

    return values.map((item) => String(item).trim()).filter(Boolean);
};

export const toStoredListFilterValue = (
    dataType: ListViewColumnDataType | undefined,
    value: ListFilterValue | null | undefined,
): ListFilterValue => {
    const values = coerceListFilterValuesForDataType(dataType, value);

    if (values.length === 0) {
        return '';
    }

    return values.length === 1 ? values[0] : values;
};

export const formatListFilterValue = (value: ListFilterValue | null | undefined, fallback = 'Set value') => {
    const values = normalizeListFilterValues(value);

    if (!values.length) {
        return fallback;
    }

    if (values.length === 1) {
        return String(values[0]);
    }

    return `${String(values[0])} +${values.length - 1}`;
};

export const getListFilterRules = (filters: ListFiltersQueryValue): ListFilterRule[] => {
    if (Array.isArray(filters)) {
        return filters;
    }

    if (Array.isArray(filters?.rules)) {
        return filters.rules;
    }

    return [];
};

export const getOperatorOptions = (dataType?: ListViewColumnDataType) => {
    if (dataType === ListViewColumnDataType.BOOLEAN) {
        return BOOLEAN_OPERATORS;
    }
    if (dataType && numericDataTypes.has(dataType)) {
        return NUMBER_OPERATORS;
    }
    if (dataType && dateDataTypes.has(dataType)) {
        return DATE_OPERATORS;
    }

    return TEXT_OPERATORS;
};

export const getColumnOperatorOptions = (column?: {
    dataType?: ListViewColumnDataType;
    filterOperators?: ListFilterOperator[];
}) => {
    const options = getOperatorOptions(column?.dataType);

    return column?.filterOperators?.length
        ? options.filter((option) => column.filterOperators?.includes(option.value))
        : options;
};

export const getListFilterDataTypeFamily = (dataType?: ListViewColumnDataType) => {
    if (dataType === ListViewColumnDataType.BOOLEAN) {
        return 'boolean';
    }
    if (dataType && numericDataTypes.has(dataType)) {
        return 'number';
    }
    if (dataType && dateDataTypes.has(dataType)) {
        return 'date';
    }

    return 'text';
};

export const canReuseListFilterValue = (
    previousDataType?: ListViewColumnDataType,
    nextDataType?: ListViewColumnDataType,
) => getListFilterDataTypeFamily(previousDataType) === getListFilterDataTypeFamily(nextDataType);

export const getInputIcon = (dataType?: ListViewColumnDataType): ReactNode => {
    if (dataType === ListViewColumnDataType.BOOLEAN) {
        return <TbCheck size='0.875rem' />;
    }
    if (dataType && numericDataTypes.has(dataType)) {
        return <TbHash size='0.875rem' />;
    }
    if (dataType && dateDataTypes.has(dataType)) {
        return <TbCalendar size='0.875rem' />;
    }

    return <TbLetterCase size='0.875rem' />;
};

const DateFilterValueInput: FC<{
    onChange?: (value: ListFilterValue) => void;
    value?: ListFilterValue;
}> = ({ onChange, value }) => (
    <PlatformDatePicker
        multiple
        onChange={(dates) => onChange?.(serializeDatePickerValues(dates))}
        placeholder='Select Date...'
        style={{ width: '100%' }}
        value={normalizeListFilterValues(value)
            .map((item) => dayjs(String(item)))
            .filter((item) => item.isValid())}
    />
);

const FilterDropdown: FC<FilterDropdownProps> = ({
    advancedFilter,
    docsLabel = 'List filters',
    emptyColumnsDescription = 'Add a visible column before filtering this View.',
    filterableResourceLabel = 'Records',
    filters,
    onAdvancedFilterChange,
    openToAdvancedFilters = false,
    setFilters,
    showAdvancedFilters = true,
    trigger,
    columns,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();
    const canUseAdvancedFilters = hasFeature('advancedFilters');
    const { listColumnKeyToIconMap } = useMaps();
    const [filterForm] = Form.useForm<{
        operator: ListFilterOperator;
        value: ListFilterValue;
        valueJoin: ListFilterValueJoin;
    }>();
    const [open, setOpen] = useState(false);
    const [columnSearch, setColumnSearch] = useState('');
    const [advancedEditorOpen, setAdvancedEditorOpen] = useState(false);
    const [advancedDraft, setAdvancedDraft] = useState<AdvancedFilterDefinition<ListFilterRule>>(() =>
        createAdvancedFilterDefinition<ListFilterRule>(),
    );

    const visibleColumns = useMemo(
        () => (columns ?? []).filter((column) => !column.isHidden).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [columns],
    );

    const filteredColumns = useMemo(() => {
        const search = columnSearch.trim().toLowerCase();

        if (!search) {
            return visibleColumns;
        }

        return visibleColumns.filter((column) =>
            [column.label, column.key, column.dataType]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(search)),
        );
    }, [columnSearch, visibleColumns]);

    const [selectedColumnKey, setSelectedColumnKey] = useState<string>();
    const selectedColumn = visibleColumns.find((column) => column.key === selectedColumnKey);
    const operatorOptions = useMemo(() => getColumnOperatorOptions(selectedColumn), [selectedColumn]);
    const operator = Form.useWatch('operator', filterForm) ?? operatorOptions[0]?.value ?? 'contains';
    const value = Form.useWatch('value', filterForm) ?? '';
    const valueJoin = Form.useWatch('valueJoin', filterForm) ?? 'and';

    useEffect(() => {
        if (selectedColumnKey && !selectedColumn) {
            setSelectedColumnKey(undefined);
            filterForm.setFieldValue('value', '');
        }
    }, [filterForm, selectedColumn, selectedColumnKey]);

    useEffect(() => {
        filterForm.setFieldsValue({
            operator: operatorOptions[0]?.value ?? 'contains',
            value: '',
            valueJoin: 'and',
        });
    }, [filterForm, operatorOptions, selectedColumnKey]);

    useEffect(() => {
        if (!open) {
            setColumnSearch('');
            setAdvancedEditorOpen(false);
            setSelectedColumnKey(undefined);
        }
    }, [open]);

    const commitFilter = async () => {
        if (!selectedColumn) {
            return;
        }

        try {
            await filterForm.validateFields(['operator', 'value', 'valueJoin']);
        } catch {
            return;
        }

        const storedValue = toStoredListFilterValue(selectedColumn.dataType, value);

        if (!hasListFilterValue(storedValue)) {
            return;
        }

        const selectedOperator = operatorOptions.find((option) => option.value === operator) ?? operatorOptions[0];
        const nextRuleBaseId = `${selectedColumn.key}-${filters.length + 1}`;
        let nextRuleId = nextRuleBaseId;
        let duplicateIndex = 1;

        while (filters.some((filter) => filter.id === nextRuleId)) {
            duplicateIndex += 1;
            nextRuleId = `${nextRuleBaseId}-${duplicateIndex}`;
        }

        const nextRules = [
            ...filters,
            {
                dataType: selectedColumn.dataType,
                field: selectedColumn.key,
                fieldLabel: selectedColumn.label,
                id: nextRuleId,
                operator,
                operatorLabel: selectedOperator.label,
                value: storedValue,
                valueJoin,
            },
        ];

        setFilters(nextRules);

        filterForm.setFieldsValue({ value: '', valueJoin: 'and' });
        setSelectedColumnKey(undefined);
    };

    const stopDropdownPropagation = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    };

    const docsFooter = <LearnMoreFooter topic={docsLabel} variant='dropdown' />;

    const renderValueInput = () => {
        if (!selectedColumn) {
            return null;
        }

        if (selectedColumn.filterValueEditor === 'company') {
            return <FinancialCompanySelect multiple={selectedColumn.allowMultipleValues} />;
        }

        if (selectedColumn.filterOptions?.length) {
            const multiple = selectedColumn.allowMultipleValues !== false;

            return (
                <FilterValueSelect
                    fallbackIcon={
                        selectedColumn.icon ??
                        listColumnKeyToIconMap[selectedColumn.key] ??
                        getInputIcon(selectedColumn.dataType)
                    }
                    fieldLabel={selectedColumn.label}
                    multiple={multiple}
                    options={selectedColumn.filterOptions}
                    placeholder='Select Values...'
                />
            );
        }

        if (selectedColumn.dataType === ListViewColumnDataType.BOOLEAN) {
            return (
                <Select
                    mode={selectedColumn.allowMultipleValues ? 'multiple' : undefined}
                    options={[
                        { label: 'True', value: true },
                        { label: 'False', value: false },
                    ]}
                    placeholder='Select Value...'
                    style={{ width: '100%' }}
                />
            );
        }

        if (numericDataTypes.has(selectedColumn.dataType)) {
            return selectedColumn.allowMultipleValues ? (
                <Select mode='tags' placeholder='Enter Values...' style={{ width: '100%' }} tokenSeparators={[',']} />
            ) : (
                <InputNumber placeholder='Enter A Value...' style={{ width: '100%' }} />
            );
        }

        if (dateDataTypes.has(selectedColumn.dataType)) {
            return <DateFilterValueInput />;
        }

        return selectedColumn.allowMultipleValues ? (
            <Select
                mode='tags'
                onInputKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        void commitFilter();
                    }
                }}
                placeholder='Enter Values...'
                style={{ width: '100%' }}
                tokenSeparators={[',']}
            />
        ) : (
            <Input maxLength={500} onPressEnter={() => void commitFilter()} placeholder='Enter A Value...' showCount />
        );
    };

    const propertyHeader = advancedEditorOpen ? (
        <DropdownNestedHeader
            avatar={{ icon: <TextQuote size='0.8125rem' /> }}
            backLabel='Back to Filterable Properties'
            onBack={() => setAdvancedEditorOpen(false)}
            title='Advanced Filters'
        />
    ) : selectedColumn ? (
        <DropdownNestedHeader
            avatar={{
                icon:
                    selectedColumn.icon ??
                    listColumnKeyToIconMap[selectedColumn.key] ??
                    getInputIcon(selectedColumn.dataType),
            }}
            backLabel='Back to Filterable Properties'
            onBack={() => setSelectedColumnKey(undefined)}
            title={selectedColumn.label}
        />
    ) : (
        <DropdownMenuLabel icon={<ListFilter size='0.875rem' />}>
            Filter {filterableResourceLabel} by Property
        </DropdownMenuLabel>
    );

    const overlay = (
        <DropdownPanel
            contentProps={{ onClick: stopDropdownPropagation }}
            footer={docsFooter}
            header={propertyHeader}
            minWidth={16 * 28}
            search={
                visibleColumns.length && !selectedColumn && !advancedEditorOpen
                    ? {
                          ariaLabel: 'Search Filterable Properties',
                          placeholder: 'Search Properties...',
                          setValue: setColumnSearch,
                          value: columnSearch,
                      }
                    : undefined
            }
            style={{ marginTop: '0.5rem' }}
        >
            {advancedEditorOpen && onAdvancedFilterChange ? (
                <div style={{ maxHeight: '36rem', overflowY: 'auto', padding: '0.75rem' }}>
                    {canUseAdvancedFilters ? (
                        <>
                            <AdvancedFilterEditor
                                definition={advancedDraft}
                                onChange={setAdvancedDraft}
                                renderAddCondition={({ disabled, onAdd }) => (
                                    <FilterDropdown
                                        columns={columns}
                                        docsLabel={docsLabel}
                                        emptyColumnsDescription={emptyColumnsDescription}
                                        filterableResourceLabel={filterableResourceLabel}
                                        filters={[]}
                                        setFilters={(nextRules) => {
                                            const nextRule = nextRules?.[0];
                                            if (nextRule) {
                                                onAdd(nextRule);
                                            }
                                        }}
                                        showAdvancedFilters={false}
                                        trigger={
                                            <Button disabled={disabled} icon={<TbPlus />} size='small' type='dashed'>
                                                Filter
                                            </Button>
                                        }
                                    />
                                )}
                                renderCondition={({ condition, remove, update }) => (
                                    <FilterButtonItem
                                        columns={columns}
                                        docsLabel={docsLabel}
                                        filter={condition.rule}
                                        filters={[condition.rule]}
                                        setFilters={(nextRules) => {
                                            const nextRule = nextRules?.[0];
                                            if (nextRule) {
                                                update(nextRule);
                                            } else {
                                                remove();
                                            }
                                        }}
                                    />
                                )}
                            />
                            <Row justify='end' style={{ marginTop: '0.75rem' }}>
                                <Button
                                    disabled={!isAdvancedFilterDefinitionComplete(advancedDraft)}
                                    icon={<TbDeviceFloppy size='1rem' />}
                                    size='small'
                                    type='primary'
                                    onClick={() => {
                                        onAdvancedFilterChange(advancedDraft);
                                        setOpen(false);
                                    }}
                                >
                                    Save Advanced Filter
                                </Button>
                            </Row>
                        </>
                    ) : (
                        <NotAvailable height='18rem' planRequired={ADVANCED_FILTERS_MINIMUM_PLAN} />
                    )}
                </div>
            ) : visibleColumns.length ? (
                selectedColumn ? (
                    <div style={{ padding: '0.75rem' }}>
                        <Form
                            component={false}
                            form={filterForm}
                            initialValues={{ operator: 'contains', value: '', valueJoin: 'and' }}
                        >
                            <Row gutter={[8, 8]}>
                                <Col span={12}>
                                    <Form.Item label='Operator' name='operator' required style={{ marginBottom: 0 }}>
                                        <Select options={operatorOptions} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label='Value'
                                        name='value'
                                        required
                                        rules={[
                                            {
                                                validator: async (_, nextValue: ListFilterValue) => {
                                                    const storedValue = toStoredListFilterValue(
                                                        selectedColumn.dataType,
                                                        nextValue,
                                                    );

                                                    if (!hasListFilterValue(storedValue)) {
                                                        throw new Error('Choose or enter a filter value.');
                                                    }
                                                },
                                            },
                                        ]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        {renderValueInput()}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row align='middle' style={{ marginTop: '0.75rem' }}>
                                <Col flex='none'>
                                    <Form.Item name='valueJoin' noStyle>
                                        <Segmented
                                            options={[
                                                { label: 'AND', value: 'and' },
                                                { label: 'OR', value: 'or' },
                                            ]}
                                            size='small'
                                        />
                                    </Form.Item>
                                </Col>
                                <Col flex='auto' />
                                <Col flex='none'>
                                    <Button
                                        disabled={
                                            !hasListFilterValue(toStoredListFilterValue(selectedColumn.dataType, value))
                                        }
                                        icon={<TbFilterPlus size='1rem' />}
                                        onClick={() => void commitFilter()}
                                        size='small'
                                        type='primary'
                                    >
                                        Add Filter
                                    </Button>
                                    <Button
                                        disabled={!filters.length}
                                        icon={<TbX size='1rem' />}
                                        onClick={() => setFilters(null)}
                                        size='small'
                                        type='text'
                                    >
                                        Clear
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                ) : (
                    <>
                        {showAdvancedFilters && onAdvancedFilterChange ? (
                            <>
                                <DropdownMenuLabel>Advanced</DropdownMenuLabel>
                                <DropdownPanelMenu selectable={false}>
                                    <Menu.Item
                                        key='advanced-filters'
                                        onClick={({ domEvent }) => {
                                            domEvent.preventDefault();
                                            domEvent.stopPropagation();
                                            if (canUseAdvancedFilters) {
                                                setAdvancedDraft(createAdvancedFilterDefinition<ListFilterRule>());
                                            }
                                            setAdvancedEditorOpen(true);
                                        }}
                                    >
                                        <DropdownOptionContent
                                            avatar={{ icon: <TextQuote size='0.8125rem' /> }}
                                            extra={
                                                <TbChevronRight
                                                    size='1rem'
                                                    style={{ color: token.colorTextTertiary, display: 'block' }}
                                                />
                                            }
                                            title='Advanced Filters'
                                        />
                                    </Menu.Item>
                                </DropdownPanelMenu>
                            </>
                        ) : null}
                        <DropdownMenuLabel>Properties</DropdownMenuLabel>
                        {filteredColumns.length ? (
                            <DropdownPanelMenu selectable={false}>
                                {filteredColumns.map((column) => (
                                    <Menu.Item
                                        key={column.id}
                                        onClick={({ domEvent }) => {
                                            domEvent.preventDefault();
                                            domEvent.stopPropagation();
                                            setSelectedColumnKey(column.key);
                                        }}
                                    >
                                        <DropdownOptionContent
                                            avatar={{
                                                icon:
                                                    column.icon ??
                                                    listColumnKeyToIconMap[column.key] ??
                                                    getInputIcon(column.dataType),
                                            }}
                                            extra={
                                                <TbChevronRight
                                                    size='1rem'
                                                    style={{ color: token.colorTextTertiary, display: 'block' }}
                                                />
                                            }
                                            title={column.label}
                                        />
                                    </Menu.Item>
                                ))}
                            </DropdownPanelMenu>
                        ) : (
                            <EmptySection
                                height='10rem'
                                icon={<TbSearch size='75%' style={{ color: token.colorTextTertiary }} />}
                                label='No Matching Properties'
                                description='Try another search term.'
                            />
                        )}
                    </>
                )
            ) : (
                <EmptySection
                    height='14rem'
                    icon={<ListFilter size='75%' style={{ color: token.colorTextTertiary }} />}
                    label='No Filterable Properties'
                    description={emptyColumnsDescription}
                />
            )}
        </DropdownPanel>
    );

    return (
        <Dropdown
            open={open}
            onOpenChange={(nextOpen, info) => {
                if (!nextOpen && info.source === 'menu') {
                    return;
                }

                if (!nextOpen) {
                    runAfterPlatformDragCancellation(() => setOpen(false));
                    return;
                }

                setOpen(true);
                if (nextOpen && openToAdvancedFilters && onAdvancedFilterChange) {
                    setAdvancedDraft(advancedFilter ?? createAdvancedFilterDefinition<ListFilterRule>());
                    setAdvancedEditorOpen(true);
                }
            }}
            popupRender={() => overlay}
            placement='bottomLeft'
            trigger={['click']}
        >
            {trigger ?? (
                <FilterEntryButton activeCount={filters.length + getAdvancedFilterCounts(advancedFilter).conditions} />
            )}
        </Dropdown>
    );
};

export default FilterDropdown;
